/**
 * Product Recommend Render (V2)
 * - DOM operations only
 * - Modal HTML is injected via innerHTML (V2 convention)
 */

import { Roles } from "../../../constants/roles";
import { Actions } from "../../../constants/actions";
import { ModalSearchBase } from "../../../components/modal-search/modal-search-base-handler";
import { ModalSearchListener } from "../../../components/modal-search/modal-search-listener";
import { translator } from "../../../translation/translator";
import commonUtil from "../../../util/common-utils";
import { SpinnerRender } from "../../../util/spinner/spinner-render";
import { RecommendBadgeIconsRender } from "../../../recommend/recommend-badge-icons-render";
import { RecommendBadgeIconsUtils } from "../../../recommend/recommend-badge-icons-utils";
import { productRecommendForm } from "./product-recommend-form";

type RecommendRankButtonVm = {
    rank: number;
    disabled: boolean;
    isCurrent: boolean;
};

export type ProductRecommendViewModel = {
    buttons: RecommendRankButtonVm[];
    canUnset: boolean;
};

export type ProductRecommendActions = {
    onPickRank?: ((rank: number) => Promise<void> | void) | null;
    onUnset?: (() => Promise<void> | void) | null;
    onClearAll?: (() => Promise<void> | void) | null;
};

type ModalSearchBaseLike = InstanceType<typeof ModalSearchBase>;

export class ProductRecommendRender {
    public base: ModalSearchBaseLike | null;

    public viewModel: ProductRecommendViewModel | null;

    public onPickRank: ((rank: number) => Promise<void> | void) | null;

    public onUnset: (() => Promise<void> | void) | null;

    public onClearAll: (() => Promise<void> | void) | null;

    public constructor() {
        this.base = null;
        this.viewModel = null;
        this.onPickRank = null;
        this.onUnset = null;
        this.onClearAll = null;
    }

    public getOverlay(): HTMLElement | null {
        return productRecommendForm.getOverlay(document);
    }

    public getBody(): HTMLElement | null {
        return productRecommendForm.getBody(document);
    }

    public getModal(): HTMLElement | null {
        return productRecommendForm.getModal(document);
    }

    public getListRoot(): HTMLElement | null {
        return productRecommendForm.getListRoot(document);
    }

    public renderLoading(): void {
        this.ensureOverlay();
        this.viewModel = null;

        if (this.base) {
            this.base.setSelectedIds([]);
            this.base.render();
        }
    }

    public setActions(actions: ProductRecommendActions): void {
        this.onPickRank = typeof actions?.onPickRank === "function" ? actions.onPickRank : null;
        this.onUnset = typeof actions?.onUnset === "function" ? actions.onUnset : null;
        this.onClearAll = typeof actions?.onClearAll === "function" ? actions.onClearAll : null;
    }

    public ensureOverlay(): void {
        if (this.getOverlay()) return;

        const t = (key: string, fallback: string): string => {
            const value = translator?.getTranslation(key);
            return value && value !== key ? value : fallback;
        };

        const html = `
            <div data-role="${Roles.PRODUCT_RECOMMEND_MODAL_OVERLAY}" class="product-recommend-modal-overlay" style="display:none">
                <div data-role="${Roles.PRODUCT_RECOMMEND_MODAL}" class="product-recommend-modal" style="display:none">
                    <div class="modal-header element-modal-header">
                        <div class="section-title-sub modal-title">
                            <span data-translate="product_recommend_rank_title">${commonUtil.escapeHtml(
                                t("product_recommend_rank_title", "Recommend rank"),
                            )}</span>
                            <span>(</span><span data-translate="entity_product">${commonUtil.escapeHtml(
                                t("entity_product", "Product"),
                            )}</span><span>)</span>
                        </div>
                        <button type="button" class="btn circle-clear modal-window element-modal-close" data-role="${Roles.PRODUCT_RECOMMEND_MODAL_CLOSE}" aria-label="${commonUtil.escapeHtmlAttribute(
                            t("product_recommend_close", "Close"),
                        )}">&times;</button>
                    </div>

                    <div class="product-recommend-modal__body" data-role="${Roles.PRODUCT_RECOMMEND_MODAL_BODY}">
                        <div class="product-recommend-modal__list" data-role="${Roles.PRODUCT_RECOMMEND_MODAL_LIST}" aria-live="polite"></div>
                    </div>

                    <div class="modal-footer element-modal-footer product-recommend-modal__footer">
                        <button type="button" class="btn delete" data-action="${Actions.SELECT_CLEAR_ALL}" data-translate="remove_all">${commonUtil.escapeHtml(
                            t("remove_all", "Remove All"),
                        )}</button>
                        <button type="button" class="btn clear" data-action="${Actions.SELECT_CLEAR}" data-translate="remove" disabled>${commonUtil.escapeHtml(
                            t("remove", "Remove"),
                        )}</button>
                    </div>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML("beforeend", html);

        try {
            translator?.updateContent?.();
        } catch {
            // ignore
        }

        const overlayEl = this.getOverlay();
        const modalEl = this.getModal();
        const listRoot = this.getListRoot();
        const closeBtnEl = productRecommendForm.getCloseButton(document);

        if (!overlayEl || !modalEl || !listRoot) return;

        this.base = new ModalSearchBase({
            overlayEl,
            modalEl,
            filterInputEl: null,
            adapter: {
                render: (base) => {
                    this.renderList(base);
                },
                handleAction: (base, action, actionEl, event) => {
                    if (action === Actions.SELECT_RANK) {
                        event.preventDefault();
                        const btn = actionEl instanceof HTMLButtonElement ? actionEl : null;
                        if (btn?.disabled) return true;

                        const rankRaw = String(actionEl.getAttribute("data-rank") || "").trim();
                        if (!rankRaw) return true;

                        if (base.hasSelectedId(rankRaw)) {
                            return true;
                        }

                        base.toggleSelectedId(rankRaw);
                        base.render();

                        const n = Number(rankRaw);
                        if (Number.isFinite(n) && n > 0) {
                            void this.onPickRank?.(Math.trunc(n));
                        }
                        return true;
                    }

                    if (action === Actions.SELECT_CLEAR) {
                        event.preventDefault();
                        const btn = actionEl instanceof HTMLButtonElement ? actionEl : null;
                        if (btn?.disabled) return true;
                        void this.onUnset?.();
                        return true;
                    }

                    if (action === Actions.SELECT_CLEAR_ALL) {
                        event.preventDefault();
                        const btn = actionEl instanceof HTMLButtonElement ? actionEl : null;
                        if (btn?.disabled) return true;

                        void this.onClearAll?.();
                        return true;
                    }

                    return false;
                },
            },
        });

        this.base.setSelectionMode("single");
        this.base.setCloseOnSelect(false);

        ModalSearchListener.setup({
            rootEl: document,
            scopeKey: "product-recommend-modal",
            overlayEl,
            modalEl,
            closeBtnEl: closeBtnEl instanceof HTMLElement ? closeBtnEl : null,
            filterInputEl: null,
            actionRootEls: [listRoot],
            base: this.base,
        });
    }

    public renderBody(viewModel: ProductRecommendViewModel): void {
        this.ensureOverlay();
        this.viewModel = viewModel;

        if (this.base) {
            const current = (Array.isArray(viewModel?.buttons) ? viewModel.buttons : []).find((b) => !!b?.isCurrent);
            const currentRank = current ? String(current.rank) : "";
            this.base.setSelectedIds(currentRank ? [currentRank] : []);
            this.base.render();
        }
    }

    public renderList(base: ModalSearchBaseLike): void {
        const listRoot = this.getListRoot();
        if (!listRoot) return;

        if (!this.viewModel) {
            const spinner = SpinnerRender.createInlineSpinner({ size: "normal" });
            listRoot.innerHTML = `
                <div class="product-recommend-modal__loading" aria-label="loading">
                    ${spinner.outerHTML}
                </div>
            `;
            return;
        }

        const buttons = Array.isArray(this.viewModel?.buttons) ? this.viewModel.buttons : [];
        const canUnset = !!this.viewModel?.canUnset;
        const recommendCaption = RecommendBadgeIconsUtils.getRecommendBadgeCaption("recommend", "Recommend");

        listRoot.innerHTML = buttons
            .map((b) => {
                const r = Math.trunc(Number(b.rank));
                if (!Number.isFinite(r) || r <= 0) return "";

                const isSelected = base.hasSelectedId(String(r));
                const selectedClass = isSelected ? " product-recommend-rank-option--selected" : "";
                const ariaPressed = isSelected ? "true" : "false";

                const disabled = !!b.disabled;
                const disabledAttr = disabled ? "disabled" : "";

                return `
                    <button type="button" class="product-recommend-rank-option${selectedClass}" data-action="${Actions.SELECT_RANK}" aria-label="${commonUtil.escapeHtmlAttribute(
                        `${recommendCaption} ${String(r)}`,
                    )}" data-rank="${commonUtil.escapeHtmlAttribute(
                        String(r),
                    )}" aria-pressed="${ariaPressed}" ${disabledAttr}>
                        <span class="product-recommend-rank-option__icon is-recommended" data-recommend-rank="${commonUtil.escapeHtmlAttribute(
                            String(r),
                        )}" aria-hidden="true">
                            ${RecommendBadgeIconsRender.recommendBadgeIconsTemplate}
                            <span class="recommend-badge__rank">${commonUtil.escapeHtml(String(r))}</span>
                        </span>
                    </button>
                `;
            })
            .filter(Boolean)
            .join("");

        const modal = this.getModal();
        if (modal) {
            const removeBtn = productRecommendForm.getRemoveButton(modal);
            if (removeBtn instanceof HTMLButtonElement) {
                removeBtn.disabled = !canUnset;
            }
        }
    }

    public open(): void {
        this.ensureOverlay();
        this.base?.open();
    }

    public close(): void {
        this.base?.close();
    }

    public setCrownState(crownEl: HTMLElement, recommended: boolean): void {
        crownEl.dataset.isRecommended = recommended ? "1" : "0";
    }

    public setCrownRank(crownEl: HTMLElement, rank: number | null): void {
        const existing = productRecommendForm.getBadgeRank(crownEl);
        const showRank = RecommendBadgeIconsUtils.shouldShowRecommendBadgeRank(crownEl);

        if (rank === null || typeof rank === "undefined") {
            delete crownEl.dataset.recommendRank;
            if (existing instanceof HTMLElement) {
                existing.remove();
            }
            return;
        }

        const n = Number(rank);
        if (!Number.isFinite(n) || n <= 0) {
            delete crownEl.dataset.recommendRank;
            if (existing instanceof HTMLElement) {
                existing.remove();
            }
            return;
        }

        crownEl.dataset.recommendRank = String(Math.trunc(n));

        if (!showRank) {
            if (existing instanceof HTMLElement) {
                existing.remove();
            }
            return;
        }

        if (existing instanceof HTMLElement) {
            existing.textContent = String(Math.trunc(n));
            return;
        }

        const el = document.createElement("span");
        el.className = "recommend-badge__rank";
        el.setAttribute("data-role", Roles.USER_RECOMMEND_BADGE_RANK);
        el.setAttribute("aria-hidden", "true");
        el.textContent = String(Math.trunc(n));
        crownEl.appendChild(el);
    }

    public setCrownLoading(crownEl: HTMLElement, loading: boolean): void {
        const existing = productRecommendForm.getBadgeSpinner(crownEl);

        if (!loading) {
            crownEl.classList.remove("loading");
            delete crownEl.dataset.loading;
            crownEl.removeAttribute("aria-busy");
            if (existing instanceof HTMLElement) {
                existing.remove();
            }
            return;
        }

        crownEl.classList.add("loading");
        crownEl.dataset.loading = "1";
        crownEl.setAttribute("aria-busy", "true");
        if (existing instanceof HTMLElement) {
            return;
        }

        const wrap = document.createElement("span");
        wrap.className = "recommend-badge__spinner";
        wrap.setAttribute("data-role", Roles.PRODUCT_RECOMMEND_BADGE_SPINNER);
        const spin = SpinnerRender.createInlineSpinner({ size: "small" });
        wrap.appendChild(spin);
        crownEl.appendChild(wrap);
    }

    public setBusy(busy: boolean): void {
        const modal = this.getModal();
        if (!modal) return;

        const btns = productRecommendForm.getModalButtons(modal);
        btns.forEach((b) => {
            if (busy) {
                if (!b.disabled) {
                    b.dataset.busyDisabled = "1";
                    b.disabled = true;
                }
                return;
            }

            if (b.dataset.busyDisabled === "1") {
                delete b.dataset.busyDisabled;
                b.disabled = false;
            }
        });
    }
}
